/**
 * frame — the armed frame's geometry, pure. The column and the sheet draw the picture that is
 * coming inside a BOUNDING BOX and let the picture's own ratio decide how much of it is used
 * (operator-verified defect, 2026-08-31: a 1536x640 landscape result was drawn 250px wide — the
 * frame was width-fixed, so a wide picture shrank to a strip while a portrait filled the column).
 *
 * The column's box is a PIXEL BUDGET (plan `2026_09_18_armed_frame_budget.md` § D1), not a fraction
 * of the viewport: chrome (the esc pill, the caption, the bar, the clearances) is measured in
 * pixels, so a fraction of `vh` either wastes room on a tall screen or overflows on a short one —
 * measured 2026-09-18, Ovid dev: opening the muse panel above the bar needed 742px of stack in a
 * 727px viewport, and the frame was squashed and cropped rather than the budget shrinking to fit.
 * `COLUMN_CHROME_PX` is that chrome's own sum, named term by term so nothing here has to restate a
 * number another file already states; `Column.module.scss` reads the same terms back as inline
 * custom properties rather than repeating them (`Column.tsx`'s own comment).
 *
 * The sheet's box is unchanged by that budget — the phone frame stays a fraction of the viewport
 * (`SHEET_FRAME_BOX_VH`), out of this plan's scope.
 *
 * The frame's width and height are set in pixels on the element that carries the ring and the glow
 * (`.result`), so the ring wraps the picture, never the box it was fitted into.
 */
import type {Aspect} from './chains'

export type Box = {width: number; height: number}

// The column's own chrome budget (plan `2026_09_18_armed_frame_budget.md` § D1), measured at
// 1352x727 (Ovid dev, Chrome, DPR 2, signed in): the esc pill's bottom edge (56) plus an 8px
// clearance, the caption (17), the two 14px stack gaps, the bar (64), and a 28px clearance above the
// viewport's bottom edge — the same 28 the bar's own anchor keeps (`Column.module.scss`'s `.col`).
export const COLUMN_TOP_CLEARANCE = 64
export const COLUMN_CAPTION_HEIGHT = 17
export const COLUMN_STACK_GAP = 14
export const COLUMN_BAR_HEIGHT = 64
export const COLUMN_BOTTOM_CLEARANCE = 28
export const COLUMN_CHROME_PX =
    COLUMN_TOP_CLEARANCE + COLUMN_CAPTION_HEIGHT + COLUMN_STACK_GAP * 2 + COLUMN_BAR_HEIGHT + COLUMN_BOTTOM_CLEARANCE

/** The frame's own floor and cap: never so short it reads as a thumbnail, never enlarged past what
 *  the tallest portrait source (1216px) tolerates well — 760 CSS px at DPR 2 is a 1.25x upscale. */
export const COLUMN_FRAME_MIN = 200
export const COLUMN_FRAME_MAX = 760
/** What the budget gives back when the counter falls back into the flow under the frame (D3) — the
 *  counter's own row height plus its gap, the same amount the beside placement never had to reserve. */
export const COLUMN_COUNTER_INFLOW_GIVEBACK = 32
export const COLUMN_BAR_MAX_VW = 0.9

/** The split counter's own flank zone (`Counter.module.scss`'s `.arrowLeft`/`.arrowRight`, second dev
 *  fix 2026-09-18, superseding this constant's own first value of 44): a 44px hit area floating 14px
 *  off the frame left a scrim-sized gap between it and the frame's own edge — a near miss returned to
 *  the wall (`onClick={onClose}` on the scrim beneath), and at either end of the chain the arrow's own
 *  `disabled` attribute swallowed the press outright rather than handing it to anything, so the LAST
 *  press of a rapid run landed on the scrim and closed the column. The zone is now 72px wide and
 *  spans the frame's full height, touching it directly (`right: 100%` / `left: 100%`, no gap) — wide
 *  enough to read as a real target, narrow enough that two of them plus the frame still clear a
 *  620px-wide 21:9 frame under a 796px viewport (`armedFrame`'s own worked example, below). */
export const COUNTER_FLANK_W = 72

/** A ratio no picture states — the frame falls back to the house portrait rather than to a
 *  division by zero when a row arrives with no stored dimensions. */
const FALLBACK_RATIO = 2 / 3

function clamp(min: number, value: number, max: number): number {
    return Math.min(Math.max(value, min), max)
}

function ratioOf(aspect: Aspect): number {
    return aspect.width > 0 && aspect.height > 0 ? aspect.width / aspect.height : FALLBACK_RATIO
}

/**
 * The largest frame of `aspect`'s ratio that fits inside `box`. Both dimensions are rounded to
 * whole pixels — a fractional frame under a 1px ring reads as a blurred edge.
 */
export function fitBox(aspect: Aspect, box: Box): Box {
    if (box.width <= 0 || box.height <= 0) return {width: 0, height: 0}
    const ratio = ratioOf(aspect)
    const width = Math.min(box.width, box.height * ratio)
    return {width: Math.round(width), height: Math.round(width / ratio)}
}

/**
 * The column's own box: `barWidth` wide (a house's own `theme.armed.barWidth`, capped at 90vw on a
 * narrow window) x the chrome budget's own leftover height, floored and capped
 * (`COLUMN_FRAME_MIN`/`MAX`). `counterInFlow` (D3) is whether the counter fell back into the stack
 * under the frame — when it did, it costs the budget `COLUMN_COUNTER_INFLOW_GIVEBACK` more than the
 * beside placement ever needed.
 */
export function columnFrameBox(viewport: Box, barWidth: number, counterInFlow: boolean): Box {
    const giveback = counterInFlow ? COLUMN_COUNTER_INFLOW_GIVEBACK : 0
    return {
        width: Math.min(barWidth, viewport.width * COLUMN_BAR_MAX_VW),
        height: clamp(COLUMN_FRAME_MIN, viewport.height - COLUMN_CHROME_PX - giveback, COLUMN_FRAME_MAX),
    }
}

export type ArmedFrame = {frame: Box; counterInFlow: boolean}

/**
 * The column's frame AND where its counter stands (D3, its width term superseded 2026-09-18 by the
 * split counter, then again the same day by the flank zones replacing it — see `COUNTER_FLANK_W`'s
 * own doc) — one decision, because the fallback depends on the frame the "beside" placement would
 * itself produce. Fits the frame assuming the counter stands beside it (no extra height reserved); if
 * the viewport is too narrow to hold both flank zones without crowding the column's own side padding,
 * refits with the counter back in the flow under the frame, which gives the frame
 * `COLUMN_COUNTER_INFLOW_GIVEBACK` less of the height budget than the beside placement needed. The
 * flank zones touch the frame directly (no more `COLUMN_STACK_GAP` term — the first split counter's
 * arrows floated `COLUMN_STACK_GAP` off the frame; the flank zones do not), so the room they need is
 * only their own width; the margin term (32) is twice the column's own 16px side padding
 * (`Column.module.scss`'s `.col`), so the frame and its flanking zones still clear the column's own
 * edges on a narrow window.
 */
export function armedFrame(aspect: Aspect, viewport: Box, barWidth: number): ArmedFrame {
    const beside = fitBox(aspect, columnFrameBox(viewport, barWidth, false))
    const roomForCounter = beside.width + 2 * COUNTER_FLANK_W + 32
    if (viewport.width >= roomForCounter) return {frame: beside, counterInFlow: false}
    return {frame: fitBox(aspect, columnFrameBox(viewport, barWidth, true)), counterInFlow: true}
}

/** `Sheet.module.scss` `.sheet`: `padding: 10px 14px 24px`. */
export const SHEET_SIDE_PADDING = 14
/** The sheet's own box height: the picture may take a little over half the viewport, no more —
 *  unchanged by the column's pixel budget (D1's own scope is the column alone). */
export const SHEET_FRAME_BOX_VH = 0.55

/** The phone sheet's box: the sheet's content width x the same frame height fraction. */
export function sheetFrameBox(viewport: Box): Box {
    return {
        width: viewport.width - 2 * SHEET_SIDE_PADDING,
        height: viewport.height * SHEET_FRAME_BOX_VH,
    }
}
